import { useRef, useState } from "react";
import { Card, Flex, InputRef } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import MetricCard from "components/MetricCard.component";
import { bookingStatusTone } from "components/bookings/bookingStatusTone";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { getRewardUsageAnalytics } from "hooks/queries/analytics";
import { useTableSort } from "hooks/useTableSort";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatCurrency, getBookingStatusLabel } from "shared/utils";
import { rewardLabel, rewardProgramLabel } from "shared/utils/loyalty";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import {
  AnalyticsRange,
  CustomDayRange,
  RewardUsageProgram,
  RewardUsageRow,
} from "shared/types";
import PATHS from "shared/constants/paths";
import { tzDate, getYtdDayRange } from "shared/utils/dayjs";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { useAuth } from "context/AuthContextProvider";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { rewardUsageSpec } from "pdf/reports/specs/archetype2";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

// Pemakaian Reward: one row per USE of a loyalty or referral reward. A
// discount is used by the purchase it was taken off, a free credit by each
// class booked with it. Discount codes are promotions, not rewards, and have
// their own report beside this one.
const RewardUsagePage = () => {
  const { state: authState } = useAuth();
  const navigate = useNavigate();
  const { sortState, onSortChange } = useTableSort();
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive =
    customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getRewardUsageAnalytics(range, customRange);

  const windowParams = customRange
    ? { from_date: customRange.from, to_date: customRange.to }
    : { range };

  const columns: ReportColumn<RewardUsageRow>[] = [
    reportColumn<RewardUsageRow>("date", {
      // Mixed on purpose: a discount is dated by its purchase, a credit by
      // its class, like every other report that lists either.
      dataIndex: "used_at",
      info: () => translate("analyticsPage.rewardUsage.col_date_info"),
      sorter: (a, b) => tzDate(a.used_at).valueOf() - tzDate(b.used_at).valueOf(),
      defaultSortOrder: "descend",
    }),
    reportColumn<RewardUsageRow>("client", {
      dataIndex: "customer_name",
      search: getColumnSearchProps<RewardUsageRow, "customer_name">({
        config: "customer_name",
        searchInput,
      }),
    }),
    reportColumn<RewardUsageRow>("program", {
      dataIndex: "program",
      render: (program: RewardUsageProgram) => rewardProgramLabel(program),
    }),
    reportColumn<RewardUsageRow>("reward", {
      key: "reward",
      // The reward as it was granted, in the wording the client detail page
      // and the loyalty report use.
      render: (_: unknown, row) => rewardLabel(row),
    }),
    reportColumn<RewardUsageRow>("used_for", {
      dataIndex: "used_for",
    }),
    reportColumn<RewardUsageRow>("discount", {
      dataIndex: "discount_amount",
      // A credit has no rupiah value, so a dash rather than Rp 0.
      render: (value: number | null) => (
        <span style={tnum}>{value === null ? "-" : `−${formatCurrency(value)}`}</span>
      ),
      sorter: (a, b) => (a.discount_amount ?? -1) - (b.discount_amount ?? -1),
    }),
    reportColumn<RewardUsageRow>("booking_status", {
      dataIndex: "status",
      // Credits only: a purchase has no booking status.
      render: (status: string | null) =>
        status ? (
          <StatusBadge tone={bookingStatusTone(status)} label={getBookingStatusLabel(status)} />
        ) : (
          "-"
        ),
    }),
  ];

  const pageCount = useReportPageCount(rewardUsageSpec, data, sortState);

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.rewardUsage.report_title")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="pemakaian-reward"
            params={windowParams}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table.length, MAX_PDF_ROWS),
                  total: data.table_total,
                },
                fileName: `spott-pemakaian-reward-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "pemakaian-reward",
                    studio: authState.business?.name ?? "",
                    data,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>{data?.range_label}</span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker
              value={isYtdActive ? null : customRange}
              onChange={setCustomRange}
            />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>

        <Flex gap={16} wrap="wrap">
          <MetricCard
            icon={<CheckCircleOutlined />}
            title={translate("analyticsPage.rewardUsage.kpi_credits_used")}
            information={translate("analyticsPage.rewardUsage.kpi_credits_used_info")}
            value={data?.credits_used.value ?? 0}
          />
          <MetricCard
            icon={<ClockCircleOutlined />}
            title={translate("analyticsPage.rewardUsage.kpi_credits_expired")}
            information={translate("analyticsPage.rewardUsage.kpi_credits_expired_info")}
            value={data?.credits_expired.value ?? 0}
          />
          <MetricCard
            icon={<PercentageOutlined />}
            title={translate("analyticsPage.rewardUsage.kpi_discount_given")}
            information={translate("analyticsPage.rewardUsage.kpi_discount_given_info")}
            value={formatCurrency(data?.discount_value_used.value ?? 0)}
          />
        </Flex>

        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<RewardUsageRow>
            serverTotal={{ total: data?.table_total, noun: translate("pdf.noun.pemakaian") }}
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            // The purchase a discount was taken off, or the reward package a
            // credit was booked from (its page lists every class booked on it).
            onRow={(row) => ({
              onClick: () => navigate(`${PATHS.CUSTOMER_PACKAGE}/${row.customer_package_id}`),
              style: { cursor: "pointer" },
            })}
            pageMemory="analyticsRewardUsage"
            onSortChange={onSortChange}
          />
        </Card>
      </Flex>
    </>
  );
};

export default RewardUsagePage;
